import React, { useState } from 'react';
import axios from 'axios';

const WearTodayModal = ({ item, token, onClose, onSuccess }) => {
  const today = new Date().toISOString().split('T')[0];
  const [sprays, setSprays] = useState(4);
  const [occasion, setOccasion] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const API = process.env.REACT_APP_API_URL;

  // Calculate new bottle percentage based on sprays
  const calculateNewPct = () => {
    if (!item.bottle_size_ml) return null;
    const sprayVolume = 0.1; // ml per spray
    const totalVolume = item.bottle_size_ml;
    const currentVolume = (item.bottle_percentage / 100) * totalVolume;
    const newVolume = Math.max(0, currentVolume - (sprays * sprayVolume));
    return Math.round((newVolume / totalVolume) * 100);
  };

  const newPct = calculateNewPct();

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Log journal entry
      await axios.post(
        `${API}/api/journal`,
        {
          fragrance_id: item.fragrance_id,
          date_worn: today,
          occasion: occasion || null,
          notes: notes || null,
          sprays: sprays,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update bottle percentage if we can calculate it
      if (newPct !== null) {
        await axios.patch(
          `${API}/api/library/${item.id}`,
          { bottle_percentage: newPct },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    }
    setLoading(false);
  };

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.85)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      backdropFilter: 'blur(4px)',
    },
    modal: {
      background: '#111111',
      border: '1px solid #2a2a2a',
      width: '100%',
      maxWidth: '460px',
      position: 'relative',
      padding: '48px',
    },
    close: {
      position: 'absolute',
      top: '20px',
      right: '24px',
      background: 'none',
      border: 'none',
      color: '#a09890',
      fontSize: '1rem',
      cursor: 'pointer',
    },
    eyebrow: {
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.7rem',
      letterSpacing: '0.4em',
      textTransform: 'uppercase',
      color: '#c9a96e',
      marginBottom: '8px',
    },
    brand: {
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.65rem',
      letterSpacing: '0.3em',
      textTransform: 'uppercase',
      color: '#c9a96e',
      marginBottom: '6px',
    },
    name: {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '2rem',
      fontWeight: 300,
      color: '#f5f0eb',
      marginBottom: '32px',
      lineHeight: 1.1,
    },
    label: {
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.65rem',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: '#a09890',
      marginBottom: '8px',
      display: 'block',
    },
    sprayRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '24px',
    },
    sprayBtn: {
      width: '40px',
      height: '40px',
      background: '#1a1a1a',
      border: '1px solid #2a2a2a',
      color: '#f5f0eb',
      fontFamily: 'Jost, sans-serif',
      fontSize: '1.2rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    sprayCount: {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '2.5rem',
      fontWeight: 300,
      color: '#c9a96e',
      minWidth: '40px',
      textAlign: 'center',
    },
    sprayLabel: {
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.75rem',
      fontWeight: 300,
      color: '#665e58',
    },
    select: {
      width: '100%',
      background: '#1a1a1a',
      border: '1px solid #2a2a2a',
      padding: '12px 16px',
      color: '#f5f0eb',
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.85rem',
      fontWeight: 300,
      marginBottom: '20px',
      outline: 'none',
      cursor: 'pointer',
      appearance: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      background: '#1a1a1a',
      border: '1px solid #2a2a2a',
      padding: '12px 16px',
      color: '#f5f0eb',
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.85rem',
      fontWeight: 300,
      marginBottom: '24px',
      outline: 'none',
      resize: 'vertical',
      height: '70px',
      boxSizing: 'border-box',
    },
    bottlePreview: {
      background: '#0e0e0e',
      border: '1px solid #2a2a2a',
      padding: '16px',
      marginBottom: '24px',
    },
    bottleLabel: {
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.6rem',
      letterSpacing: '0.3em',
      textTransform: 'uppercase',
      color: '#665e58',
      marginBottom: '10px',
      display: 'flex',
      justifyContent: 'space-between',
    },
    bottleBar: {
      height: '2px',
      background: '#2a2a2a',
      marginBottom: '6px',
    },
    bottleBarFill: (pct) => ({
      height: '2px',
      background: pct > 30 ? '#c9a96e' : '#e07070',
      width: `${pct}%`,
      transition: 'width 0.3s ease',
    }),
    bottleChange: {
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.7rem',
      fontWeight: 300,
      color: '#a09890',
      textAlign: 'right',
    },
    button: {
      width: '100%',
      background: '#c9a96e',
      border: 'none',
      padding: '16px',
      color: '#0a0a0a',
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.75rem',
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      cursor: 'pointer',
    },
    error: {
      color: '#e07070',
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.8rem',
      marginBottom: '16px',
    },
    noSize: {
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.7rem',
      fontWeight: 300,
      color: '#665e58',
      fontStyle: 'italic',
      marginBottom: '24px',
    },
  };

  return (
    <div style={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={styles.modal}>
        <button style={styles.close} onClick={onClose}>✕</button>

        <p style={styles.eyebrow}>Wearing Today</p>
        <p style={styles.brand}>{item.brand}</p>
        <p style={styles.name}>{item.name}</p>

        {/* Spray counter */}
        <label style={styles.label}>How many sprays?</label>
        <div style={styles.sprayRow}>
          <button
            style={styles.sprayBtn}
            onClick={() => setSprays(s => Math.max(1, s - 1))}
          >−</button>
          <span style={styles.sprayCount}>{sprays}</span>
          <button
            style={styles.sprayBtn}
            onClick={() => setSprays(s => Math.min(20, s + 1))}
          >+</button>
          <span style={styles.sprayLabel}>sprays</span>
        </div>

        {/* Bottle preview */}
        {newPct !== null ? (
          <div style={styles.bottlePreview}>
            <div style={styles.bottleLabel}>
              <span>Bottle Level</span>
              <span style={{ color: newPct > 30 ? '#c9a96e' : '#e07070' }}>{newPct}%</span>
            </div>
            <div style={styles.bottleBar}>
              <div style={styles.bottleBarFill(newPct)} />
            </div>
            <p style={styles.bottleChange}>
              {item.bottle_percentage}% → {newPct}% after {sprays} spray{sprays !== 1 ? 's' : ''}
            </p>
          </div>
        ) : (
          <p style={styles.noSize}>
            Add a bottle size to your library entry to track usage automatically
          </p>
        )}

        {/* Occasion */}
        <label style={styles.label}>Occasion (optional)</label>
        <select style={styles.select} value={occasion} onChange={e => setOccasion(e.target.value)}>
          <option value="">Select...</option>
          <option>Casual</option>
          <option>Office</option>
          <option>Evening</option>
          <option>Date</option>
          <option>Sport</option>
          <option>Night Out</option>
        </select>

        {/* Notes */}
        <label style={styles.label}>Quick note (optional)</label>
        <textarea
          style={styles.textarea}
          placeholder="Any thoughts for today..."
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />

        {error && <p style={styles.error}>{error}</p>}

        <button style={styles.button} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Logging...' : `Log ${sprays} Spray${sprays !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
};

export default WearTodayModal;